/* ============================================================================
   HR ARTICLE SYSTEM  ·  hr-article-system.js
   Shared behaviour for the five blog article archetypes.

   Everything is opt-in per root element ([data-hrat]) and every DOM change is
   idempotent, so a re-run (theme editor re-render) cannot double up.

   Reveal note: content is VISIBLE by default in CSS. The script adds .hrat-anim
   only when it also installs an observer, and a timeout failsafe forces .is-in
   regardless. A never-firing observer can never leave an article blank.
   ========================================================================== */
(function () {
  'use strict';

  function slug(text) {
    return String(text || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60);
  }

  function textOf(node) {
    return (node.textContent || '').trim();
  }

  /* --------------------------------------------------------- headings --- */
  function headingIds(body) {
    var hs = body.querySelectorAll('h2, h3');
    Array.prototype.forEach.call(hs, function (h, i) {
      if (!h.id) {
        var s = slug(textOf(h));
        h.id = 'hrat-' + (s || 'section') + '-' + (i + 1);
      }
    });
    return hs;
  }

  /* ------------------------------------------------------------ tables --- */
  /* The science archetype promotes its tables to numbered figures, so they have
     to be legible at 390 as figures. The legacy .hr-tablescroll wrapper does not
     actually scroll at 390: it crushes the first column to one character per
     line instead. Hand those tables to the system's own row-card transform. */
  function adoptLegacyTables(body) {
    Array.prototype.forEach.call(body.querySelectorAll('.hr-tablescroll, .hr-tablewrap'), function (w) {
      var t = w.querySelector('table');
      if (!t) return;
      t.classList.remove('hr-table');
      t.setAttribute('data-hrat-adopted', '');
      w.parentNode.insertBefore(t, w);
      if (!w.querySelector('table')) w.parentNode.removeChild(w);
    });
  }

  function normalizeTables(root) {
    var tables = root.querySelectorAll('table');
    Array.prototype.forEach.call(tables, function (table) {
      /* Tables authored with the existing .hr-table component already have their
         own scroll wrapper and styling. Leave them exactly as they are. */
      if (table.classList.contains('hr-table') || table.closest('.hr-tablewrap, .hr-tablescroll, .hr-fig')) return;
      if (!table.closest('.hrat-table-shell')) {
        var shell = document.createElement('div');
        shell.className = 'hrat-table-shell';
        table.parentNode.insertBefore(shell, table);
        shell.appendChild(table);
      }
      var heads = table.querySelectorAll('thead th');
      if (!heads.length) return;
      var labels = Array.prototype.map.call(heads, textOf);

      /* Only wide or wordy tables become stacked row-cards on mobile. A tight
         3-column table (etymology, short specs) reads better as a compact table
         at every width, and stacking it just repeats its labels. */
      var shellEl = table.closest('.hrat-table-shell');
      var longCell = false;
      Array.prototype.forEach.call(table.querySelectorAll('tbody td, tbody th'), function (c) {
        if (textOf(c).length > 40) longCell = true;
      });
      if (shellEl && (labels.length >= 4 || longCell)) {
        shellEl.setAttribute('data-hrat-stack', '');
      }
      /* Six or more columns never fit a 680px column either: those stack at
         every width. A three-column A-versus-B table keeps both column names
         on each stacked row, otherwise the second side loses its label. */
      var firstRow = table.querySelector('tbody tr');
      var firstIsRank = !!(firstRow && firstRow.children[0] && /^\d+$/.test(textOf(firstRow.children[0])));
      if (shellEl && labels.length >= 6) shellEl.setAttribute('data-hrat-wide', '');
      if (shellEl && labels.length === 3 && !firstIsRank && labels[1] && labels[2]) shellEl.setAttribute('data-hrat-pair', '');
      Array.prototype.forEach.call(table.querySelectorAll('tbody tr'), function (tr, rowIndex) {
        var cells = tr.children;
        /* A leading pure-number column is a rank, not a field. On the stacked
           mobile card it becomes the card's own number instead of a labelled
           row, and the next cell becomes the card title. */
        var hasRank = cells[0] && /^\d+$/.test(textOf(cells[0]));
        if (hasRank) {
          tr.setAttribute('data-hrat-rank', textOf(cells[0]));
          cells[0].classList.add('hrat-rankcell');
        }
        var nameIndex = hasRank ? 1 : 0;
        if (cells[nameIndex]) cells[nameIndex].classList.add('hrat-namecell');
        if (cells[nameIndex + 1]) {
          cells[nameIndex + 1].classList.add('hrat-valuecell');
          /* a value that is a sentence is prose, not a figure: it drops out
             of the mono/amber slot onto its own line in body type */
          if (textOf(cells[nameIndex + 1]).length > 40) cells[nameIndex + 1].setAttribute('data-hrat-long', '');
        }
        for (var c = nameIndex + 2; c < cells.length; c++) cells[c].classList.add('hrat-notecell');
        Array.prototype.forEach.call(cells, function (cell, i) {
          if (labels[i] && !cell.hasAttribute('data-hrat-th')) {
            cell.setAttribute('data-hrat-th', labels[i]);
          }
        });
        /* Break the tiling deliberately: the first row and any row the copy
           itself calls an outlier read differently from the quiet middle. */
        if (rowIndex === 0) tr.setAttribute('data-hrat-lead-row', '');
        var rowText = textOf(tr);
        if (rowText.toLowerCase().indexOf('outlier') !== -1 || tr.classList.contains('is-hero')) {
          tr.setAttribute('data-hrat-callout', '');
        }
      });

      /* The value column label belongs on the card once, as a legend, not
         repeated on all twelve rows. */
      var shell = table.closest('.hrat-table-shell');
      var valueLabel = labels[
        /^\d+$/.test(textOf((table.querySelector('tbody tr') || table).children[0] || table)) ? 2 : 1
      ];
      if (shell && valueLabel && !shell.querySelector('.hrat-table-legend')) {
        var legend = document.createElement('p');
        legend.className = 'hrat-table-legend';
        legend.textContent = valueLabel;
        shell.insertBefore(legend, shell.firstChild);
      }
    });
  }

  /* ------------------------------------------------ ranked: rung board --- */
  function buildRankboard(root, body) {
    var slot = root.querySelector('[data-hrat-rank-slot]');
    if (!slot || slot.getAttribute('data-hrat-filled') === 'true') return;

    var table = null;
    Array.prototype.some.call(body.querySelectorAll('table'), function (t) {
      if (t.querySelectorAll('tbody tr').length >= 3) { table = t; return true; }
      return false;
    });
    if (!table) { slot.hidden = true; return; }

    var rows = table.querySelectorAll('tbody tr');
    if (rows.length < 3) { slot.hidden = true; return; }

    /* Wide scoring tables: name plus the score column, nothing else. The
       score column is the one headed "score", "per 100", "points", "index"
       or "total", else the last mostly-numeric column. */
    var heads = Array.prototype.map.call(table.querySelectorAll('thead th'), textOf);
    var wide = heads.length > 4;
    var scoreIdx = -1;
    if (wide) {
      heads.forEach(function (h, i) { if (/score|per 100|points|index|total|rank(?:ing)?(?!.*#)/i.test(h) && i > 1) scoreIdx = i; });
      if (scoreIdx < 0) {
        for (var ci = heads.length - 1; ci > 1; ci--) {
          var numeric = 0;
          Array.prototype.forEach.call(rows, function (r) { if (r.children[ci] && /\d/.test(textOf(r.children[ci]))) numeric++; });
          if (numeric >= rows.length / 2) { scoreIdx = ci; break; }
        }
      }
    }

    var list = document.createElement('ol');
    list.className = 'hrat-rung';

    Array.prototype.forEach.call(rows, function (tr, index) {
      var cells = Array.prototype.map.call(tr.children, textOf);
      if (!cells.length) return;
      /* Column shapes vary: [rank, name, value, note] or [name, value, note]. */
      var lead = cells[0] || '';
      var isRankCol = /^\d+$/.test(lead) || lead === '-';
      var rank = isRankCol ? lead : String(index + 1);
      var rest = isRankCol ? cells.slice(1) : cells;
      var name = rest[0] || '';
      var value = rest[1] || '';
      var note = rest.slice(2).join(' ');
      if (wide) {
        value = scoreIdx >= 0 ? (cells[scoreIdx] || '') : '';
        note = '';
      }

      var li = document.createElement('li');
      var n = document.createElement('span');
      n.className = 'hrat-rung__n';
      n.textContent = rank === '-' ? '--' : (rank.length < 2 ? '0' + rank : rank);
      var d = document.createElement('div');

      var nm = document.createElement('p');
      nm.className = 'hrat-rung__name';
      nm.textContent = name;
      d.appendChild(nm);

      if (value) {
        var v = document.createElement('p');
        v.className = 'hrat-rung__v';
        v.textContent = value;
        d.appendChild(v);
      }
      if (note) {
        var nt = document.createElement('p');
        nt.className = 'hrat-rung__note';
        nt.textContent = note;
        d.appendChild(nt);
      }
      li.appendChild(n);
      li.appendChild(d);
      list.appendChild(li);
    });

    if (!list.children.length) { slot.hidden = true; return; }
    slot.appendChild(list);
    slot.setAttribute('data-hrat-filled', 'true');
    slot.hidden = false;
  }

  /* -------------------------------------------- guide: the short version -- */
  function buildShortVersion(root, body) {
    var slot = root.querySelector('[data-hrat-short-slot]');
    if (!slot || slot.getAttribute('data-hrat-filled') === 'true') return;

    var target = null;
    Array.prototype.forEach.call(body.querySelectorAll('h2, h3'), function (h) {
      if (target) return;
      var t = textOf(h).toLowerCase();
      if (/short version|in short|key takeaway|the bottom line|quick answer/.test(t)) target = h;
    });

    var paras = [];
    if (target) {
      var node = target.nextElementSibling;
      while (node && !/^H[23]$/.test(node.tagName) && paras.length < 2) {
        if (node.tagName === 'P') paras.push(node.textContent);
        node = node.nextElementSibling;
      }
    }
    if (!paras.length) {
      /* Fall back to a bolded summary sentence, then to the first real
         paragraph of the post whatever wrapper it happens to sit in. */
      var strong = body.querySelector('p > strong');
      if (strong && textOf(strong).length > 60) {
        paras.push(strong.textContent);
      } else {
        var all = body.querySelectorAll('p');
        for (var i = 0; i < all.length; i++) {
          var t = textOf(all[i]);
          if (t.length > 90 && !/^by\s+\S/i.test(t)) {
            /* two sentences is a summary; a whole opening paragraph is not */
            var parts = t.split('. ');
            var cut = parts.length > 2 ? parts.slice(0, 2).join('. ') + '.' : t;
            paras.push(cut);
            break;
          }
        }
      }
    }
    if (!paras.length) {
      var emptyCard = slot.closest('.hrat__short');
      if (emptyCard) emptyCard.hidden = true;
      slot.hidden = true;
      return;
    }

    paras.forEach(function (t) {
      var p = document.createElement('p');
      p.textContent = String(t).trim();
      slot.appendChild(p);
    });
    slot.setAttribute('data-hrat-filled', 'true');
    slot.hidden = false;
  }

  /* ------------------------------------------------- guide: steps as TOC -- */
  function buildSteps(root, headings) {
    var slot = root.querySelector('[data-hrat-steps-slot]');
    if (!slot || slot.getAttribute('data-hrat-filled') === 'true') return;

    var h2s = Array.prototype.filter.call(headings, function (h) {
      return h.tagName === 'H2' && textOf(h);
    });
    if (h2s.length < 3) { slot.hidden = true; return; }

    var ol = document.createElement('ol');
    h2s.forEach(function (h) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = textOf(h);
      li.appendChild(a);
      ol.appendChild(li);
    });
    slot.appendChild(ol);
    slot.setAttribute('data-hrat-filled', 'true');
    slot.hidden = false;
  }

  /* ----------------------------------------------- story: drop cap + quote -- */
  /* The chrome already prints the author. A leading "By Name" line in the post
     body is then a duplicate byline, so hide it (never delete the node). */
  function dedupeByline(body) {
    var ps = body.querySelectorAll('p');
    for (var i = 0; i < Math.min(2, ps.length); i++) {
      var t = textOf(ps[i]);
      if (t.length < 60 && /^by\s+\S/i.test(t)) { ps[i].hidden = true; break; }
    }
  }

  function storyBits(root, body) {
    if (root.classList.contains('hrat--story')) {
      /* The first paragraph is often a byline line ("By Dr ..."). Drop-cap the
         first paragraph that is actually prose. */
      var ps = body.querySelectorAll('.hr-body > p');
      for (var i = 0; i < ps.length; i++) {
        if (textOf(ps[i]).length > 120) { ps[i].classList.add('hrat-dropcap'); break; }
      }
    }
    var quote = root.querySelector('[data-hrat-quote]');
    if (!quote || quote.getAttribute('data-hrat-placed') === 'true') return;
    var paras = body.querySelectorAll('.hr-body > p');
    var after = paras[3] || paras[paras.length - 1];
    if (!after) return;
    after.parentNode.insertBefore(quote, after.nextSibling);
    quote.setAttribute('data-hrat-placed', 'true');
  }

  /* The science opener carries a mono annotation. When the post has no editor
     written one, state what the piece actually is: reading time, how many
     sections, how many figures. Measured, never invented. */
  function metaRail(root, body) {
    var slot = root.querySelector('[data-hrat-meta]');
    if (!slot || slot.getAttribute('data-hrat-filled') === 'true') return;
    var words = textOf(body).split(/\s+/).length;
    var mins = Math.max(1, Math.round(words / 200));
    var sections = body.querySelectorAll('h2').length;
    /* Count FIGURES, not "objects". buildFigures() has already promoted every
       table to a numbered .hr-fig, so this noun is now true. */
    var figures = body.querySelectorAll('.hr-fig').length;
    var bits = [mins + ' min read'];
    if (sections) bits.push(sections + (sections === 1 ? ' section' : ' sections'));
    if (figures) bits.push(figures + (figures === 1 ? ' figure' : ' figures'));
    slot.textContent = bits.join('  /  ');
    slot.setAttribute('data-hrat-filled', 'true');
    slot.hidden = false;
  }

  /* ------------------------------------------------ science: figures ----- */
  /* The stored science bodies contain TABLES, not charts. Nothing is invented
     here: every table already in the body is promoted to a numbered figure with
     a mono caption derived from the nearest preceding heading, so the meta
     rail's figure count is true instead of counting tables and calling them
     figures. */
  function buildFigures(root, body) {
    if (!root.classList.contains('hrat--science')) return 0;
    var tables = body.querySelectorAll('table');
    var n = 0;
    Array.prototype.forEach.call(tables, function (table) {
      var unit = table.closest('.hrat-table-shell, .hr-tablescroll, .hr-tablewrap') || table;
      if (unit.closest('.hr-fig')) { n++; return; }
      n++;
      var fig = document.createElement('figure');
      fig.className = 'hr-fig hrat__fig';
      fig.setAttribute('data-hrat-fig', String(n));
      unit.parentNode.insertBefore(fig, unit);
      fig.appendChild(unit);

      /* Caption text comes from the nearest preceding heading, never invented. */
      var label = '';
      var probe = fig.previousElementSibling;
      while (probe && !label) {
        if (/^H[2-4]$/.test(probe.tagName)) label = textOf(probe);
        probe = probe.previousElementSibling;
      }
      var up = fig.parentNode;
      while (up && up !== body && !label) {
        var sib = up.previousElementSibling;
        while (sib && !label) {
          if (/^H[2-4]$/.test(sib.tagName)) label = textOf(sib);
          sib = sib.previousElementSibling;
        }
        up = up.parentNode;
      }
      var cap = document.createElement('figcaption');
      cap.className = 'hrat__figcap';
      var b = document.createElement('b');
      b.textContent = 'Figure ' + n;
      cap.appendChild(b);
      if (label) cap.appendChild(document.createTextNode(' ' + label));
      fig.appendChild(cap);
    });
    return n;
  }

  /* ------------------------------------------------- science: sources ---- */
  /* Built from the links the copy already cites. Only hrefs that leave
     humanrenaissance.ca count, each listed once. */
  function buildSources(root, body) {
    if (!root.classList.contains('hrat--science')) return;
    var wrap = root.querySelector('.hrat__wrap');
    if (!wrap || root.querySelector('.hrat__sources')) return;

    var seen = {};
    var items = [];
    var here = location.hostname.replace(/^www\./, '');
    Array.prototype.forEach.call(body.querySelectorAll('a[href]'), function (a) {
      var href = a.getAttribute('href') || '';
      if (!/^https?:/i.test(href)) return;
      var host;
      try { host = new URL(href, location.href).hostname.replace(/^www\./, ''); }
      catch (e) { return; }
      if (!host || host === here) return;
      if (/(^|\.)humanrenaissance\.ca$/i.test(host)) return;
      var key = href.split('#')[0];
      if (seen[key]) return;
      seen[key] = true;
      items.push({ href: href, host: host, text: textOf(a) });
    });
    if (!items.length) return;

    var aside = document.createElement('aside');
    aside.className = 'hrat__sources hrat__reveal';
    var h = document.createElement('h2');
    h.textContent = 'Sources';
    aside.appendChild(h);
    var ol = document.createElement('ol');
    items.forEach(function (it) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = it.href;
      a.rel = 'noopener nofollow';
      a.target = '_blank';
      a.textContent = it.text && it.text.length > 3 ? it.text : it.host;
      li.appendChild(a);
      var s = document.createElement('span');
      s.className = 'hrat__source-host';
      s.textContent = '  ' + it.host;
      li.appendChild(s);
      ol.appendChild(li);
    });
    aside.appendChild(ol);

    var bodyWrap = root.querySelector('[data-hrat-body]');
    if (bodyWrap && bodyWrap.parentNode === wrap) {
      wrap.insertBefore(aside, bodyWrap.nextSibling);
    } else {
      wrap.appendChild(aside);
    }
  }

  /* ------------------------------------------- long read: sections jump -- */
  function buildJump(root, headings, words) {
    if (root.querySelector('.hrat__jump')) return;
    if (words < 4000) return;
    var h2s = Array.prototype.filter.call(headings, function (h) {
      return h.tagName === 'H2' && textOf(h);
    });
    if (h2s.length < 4) return;

    var nav = document.createElement('nav');
    nav.className = 'hrat__jump';
    nav.setAttribute('aria-label', 'Sections');

    var panel = document.createElement('ol');
    panel.className = 'hrat__jump-panel';
    panel.id = 'hrat-jump-panel';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hrat__jump-btn';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', 'hrat-jump-panel');
    btn.innerHTML =
      '<span>Sections <span class="hrat__jump-count">' + h2s.length + '</span></span>' +
      '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" ' +
      'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M3 10l5-5 5 5"/></svg>';

    function close() {
      nav.removeAttribute('data-open');
      btn.setAttribute('aria-expanded', 'false');
    }

    h2s.forEach(function (h) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = textOf(h);
      a.addEventListener('click', close);
      li.appendChild(a);
      panel.appendChild(li);
    });

    btn.addEventListener('click', function () {
      if (nav.hasAttribute('data-open')) { close(); return; }
      nav.setAttribute('data-open', '');
      btn.setAttribute('aria-expanded', 'true');
    });
    document.addEventListener('click', function (e) {
      if (!nav.contains(e.target)) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    nav.appendChild(panel);
    nav.appendChild(btn);
    root.appendChild(nav);
  }

  /* -------------------------------------------------- sticky header var -- */
  /* scroll-margin-top has to clear the REAL header, not a guess. The theme
     header is hide-on-scroll-down / show-on-scroll-up: arriving at an anchor is
     clean because it is retracted, but any upward nudge re-reveals it over the
     heading. Measure it and publish the height as --hrat-header-h on :root. */
  function headerVar() {
    /* The header GROUP is several sections (announcement bar, header, ...).
       The first one is usually zero-height, so take the tallest sticky/fixed
       member, not querySelector's first hit. */
    var els = document.querySelectorAll(
      '.shopify-section-group-header-group, .section-header, header.header-wrapper, #shopify-section-header'
    );
    var h = 0;
    Array.prototype.forEach.call(els, function (el) {
      var cs = getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') return;
      var r = el.getBoundingClientRect().height;
      if (r > h) h = r;
    });
    if (!h || h < 40 || h > 260) h = 92;
    document.documentElement.style.setProperty('--hrat-header-h', Math.round(h) + 'px');
  }

  /* ------------------------------------------------------------ reveal --- */
  function reveal(root) {
    var items = root.querySelectorAll('.hrat__reveal');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) return;

    Array.prototype.forEach.call(items, function (el) { el.classList.add('hrat-anim'); });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          en.target.classList.add('is-in');
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

    Array.prototype.forEach.call(items, function (el) { io.observe(el); });
    /* Failsafe: nothing stays invisible waiting on an observer. */
    setTimeout(function () {
      Array.prototype.forEach.call(items, function (el) { el.classList.add('is-in'); });
    }, 1600);
  }

  /* ---------------------------------------------------------- progress --- */
  function progress(root) {
    var bar = root.querySelector('[data-hrat-bar]');
    var body = root.querySelector('[data-hrat-body]');
    if (!bar || !body) return;
    var ticking = false;
    function update() {
      var r = body.getBoundingClientRect();
      var total = r.height - window.innerHeight;
      var done = total > 0 ? Math.max(0, Math.min(1, -r.top / total)) : 0;
      bar.style.transform = 'scaleX(' + done + ')';
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  function init() {
    headerVar();
    Array.prototype.forEach.call(document.querySelectorAll('[data-hrat]'), function (root) {
      var body = root.querySelector('[data-hrat-body]');
      if (body) {
        var headings = headingIds(body);
        if (root.classList.contains('hrat--science')) adoptLegacyTables(body);
        normalizeTables(body);
        buildFigures(root, body);
        buildSources(root, body);
        buildRankboard(root, body);
        buildShortVersion(root, body);
        buildSteps(root, headings);
        storyBits(root, body);
        dedupeByline(body);
        metaRail(root, body);
        buildJump(root, headings, textOf(body).split(/\s+/).length);
      }
      reveal(root);
      progress(root);
    });

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(headerVar, 150);
    }, { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(headerVar).catch(function () {});
    }
    window.addEventListener('load', headerVar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
